import React, { useRef, useState } from "react";
import { X, RefreshCw } from "lucide-react";


export default function ClipWizard({
  isOpen,
  onClose,
  song,
  step,
  setStep,
  uploadedPhoto,
  onPhotoUpload,
  aspectRatio,
  onSelectAspectRatio,
  frames,
  scenario,
  selectedFrame,
  onSelectFrame,
  currentTask,
  generatingStory,
  onGenerateStory,
  onDrawFrame,
  onDrawAllFrames,
  onGenerateVideos,
  onRegenerateVideo,
  onRegenerateImage
}) {
  const fileInputRef = useRef(null);
  const [drawingFrame, setDrawingFrame] = useState(null);
  const [regeneratingImage, setRegeneratingImage] = useState(null);
  
  if (!isOpen) return null;
  
  // Calculate progress stats
  const totalFrames = frames.length;
  const imagesReady = frames.filter(f => f.status === 'image_ready' || f.status === 'video_ready').length;
  const videosReady = frames.filter(f => f.status === 'video_ready').length;
  const imagesFailed = frames.filter(f => f.status === 'failed').length;
  const generatingImages = frames.filter(f => f.status === 'generating_image').length;
  const generatingVideos = frames.filter(f => f.status === 'generating_video').length;
  const anyRegenerationInProgress = frames.some(f => f.regeneration_in_progress);
  
  const allImagesReady = totalFrames > 0 && imagesReady === totalFrames;
  const allVideosReady = totalFrames > 0 && videosReady === totalFrames;
  const someFramesFailed = imagesFailed > 0;
  
  // Check if we're in the middle of processing
  const isDrawingImages = currentTask?.status === 'drawing_images' || generatingImages > 0;
  const isGeneratingVideos = currentTask?.status === 'generating_videos' || currentTask?.status === 'stitching' || generatingVideos > 0;
  
  // Calculate progress percentages
  const imageProgress = totalFrames > 0 ? Math.round((imagesReady / totalFrames) * 100) : 0;
  const videoProgress = totalFrames > 0 ? Math.round((videosReady / totalFrames) * 100) : 0;
  
  // Determine effective step based on progress
  // Step 1: Upload Photo
  // Step 2: Select Aspect Ratio
  // Step 3: Draw Frames (images)
  // Step 4: Generate Videos
  // Step 5: Complete
  let effectiveStep = step;
  
  // If we have a currentTask (clip already created), determine step from its state
  if (currentTask) {
    if (allVideosReady || currentTask?.status === 'completed') {
      effectiveStep = 5;
    } else if (isGeneratingVideos || (allImagesReady && !allVideosReady)) {
      effectiveStep = 4;
    } else if (isDrawingImages || imagesReady > 0 || totalFrames > 0) {
      effectiveStep = 3;
    } else {
      effectiveStep = 3; // If task exists but no frames, still show step 3
    }
  } else {
    // No task yet - use the step state
    if (step === 1) {
      effectiveStep = 1;
    } else if (step === 2) {
      effectiveStep = 2;
    }
  }
  
  const handleDrawSingleFrame = async (frameNum) => {
    setDrawingFrame(frameNum);
    await onDrawFrame(frameNum);
    setDrawingFrame(null);
  };
  
  return (
    <div className="video-wizard-overlay" data-testid="clip-wizard">
      <div className="video-wizard video-wizard-wide">
        <div className="video-wizard-header">
          <h2>Generate Clip</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        
        {/* Progress Steps */}
        <div className="video-wizard-steps">
          <div className={`wizard-step ${effectiveStep >= 1 ? 'active' : ''} ${effectiveStep > 1 ? 'done' : ''}`}>
            <div className="step-number">1</div>
            <span>Upload Photo</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${effectiveStep >= 2 ? 'active' : ''} ${effectiveStep > 2 ? 'done' : ''}`}>
            <div className="step-number">2</div>
            <span>Aspect Ratio</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${effectiveStep >= 3 ? 'active' : ''} ${effectiveStep > 3 ? 'done' : ''}`}>
            <div className="step-number">3</div>
            <span>Draw Frames</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${effectiveStep >= 4 ? 'active' : ''} ${effectiveStep > 4 ? 'done' : ''}`}>
            <div className="step-number">4</div>
            <span>Generate Videos</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${effectiveStep >= 5 ? 'active' : ''}`}>
            <div className="step-number">5</div>
            <span>Complete</span>
          </div>
        </div>
        
        <div className="video-wizard-content">
          {/* Step 1: Upload Photo - only show if no currentTask */}
          {effectiveStep === 1 && !currentTask && (
            <div className="wizard-step-content">
              <p className="wizard-step-desc">Upload a photo that will be used for scenes with an actor. This will be transformed for each scene.</p>
              
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/jpeg,image/jpg,image/png" 
                onChange={onPhotoUpload}
                style={{ display: 'none' }}
              />
              
              {!uploadedPhoto ? (
                <div 
                  className="photo-upload-area"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="clip-photo-upload-area"
                >
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="upload-icon-svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>Click to upload photo</span>
                  <span className="upload-hint">JPG, JPEG, or PNG</span>
                </div>
              ) : (
                <div className="photo-preview-container">
                  <img src={uploadedPhoto.preview} alt="Preview" className="photo-preview" />
                  <button 
                    className="btn-outline" 
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  >
                    Change Photo
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: Select Aspect Ratio - only show if no currentTask */}
          {effectiveStep === 2 && !currentTask && (
            <div className="wizard-step-content">
              <p className="wizard-step-desc">Select the aspect ratio for your video clip. This will determine the orientation of all generated frames.</p>
              
              <div className="aspect-ratio-selector" data-testid="aspect-ratio-selector">
                <button
                  className={`aspect-ratio-btn ${aspectRatio === '16:9' ? 'selected' : ''}`}
                  onClick={() => onSelectAspectRatio('16:9')}
                  data-testid="aspect-ratio-16-9"
                >
                  <div className="aspect-ratio-shape horizontal">
                    <span>16:9</span>
                  </div>
                  <span className="aspect-ratio-label">Horizontal</span>
                </button>
                
                <button
                  className={`aspect-ratio-btn ${aspectRatio === '9:16' ? 'selected' : ''}`}
                  onClick={() => onSelectAspectRatio('9:16')}
                  data-testid="aspect-ratio-9-16"
                >
                  <div className="aspect-ratio-shape vertical">
                    <span>9:16</span>
                  </div>
                  <span className="aspect-ratio-label">Vertical</span>
                </button>
              </div>
            </div>
          )}
          
          {/* Step 3 & 4: Draw Frames / Generate Videos */}
          {(effectiveStep === 3 || effectiveStep === 4) && (
            <div className="wizard-step-content clip-frames-step">
              {/* Scenario Display */}
              {scenario && (
                <div className="clip-scenario-box">
                  <h4>📖 Story Plot</h4>
                  <p>{scenario}</p>
                </div>
              )}
              
              <div className="frames-header">
                <div className="frames-info">
                  <span className="frames-count">{frames.length} Scenes</span>
                  {currentTask && (
                    <span className={`status-badge status-${currentTask.status}`}>
                      {currentTask.status.replace(/_/g, ' ')}
                    </span>
                  )}
                  {/* Progress indicator */}
                  {(isDrawingImages || isGeneratingVideos) && (
                    <span className="progress-indicator">
                      {isDrawingImages && `Images: ${imagesReady}/${totalFrames}`}
                      {isGeneratingVideos && `Videos: ${videosReady}/${totalFrames}`}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="frames-grid">
                {frames.map((frame, idx) => (
                  <div 
                    key={idx}
                    className={`frame-card ${selectedFrame === idx ? 'frame-card-selected' : ''} frame-status-${frame.status}`}
                    onClick={() => onSelectFrame(idx)}
                  >
                    <div className="frame-number">#{frame.segment_number}</div>
                    <div className="frame-time">{frame.start_time} - {frame.end_time}</div>
                    {frame.image_url ? (
                      <div className="frame-thumbnail">
                        <img src={frame.image_url} alt={`Frame ${frame.segment_number}`} />
                      </div>
                    ) : (
                      <div className="frame-placeholder">
                        {frame.status === 'generating_image' ? (
                          <span className="spinner" />
                        ) : frame.status === 'generating_video' ? (
                          <><span className="spinner" /><span className="text-xs">Video</span></>
                        ) : frame.status === 'failed' ? (
                          <span className="text-red">Failed</span>
                        ) : (
                          <span className="subtle">No image</span>
                        )}
                      </div>
                    )}
                    <div className="frame-actor-badge">
                      {frame.with_actor ? '👤 Actor' : '🎬 Scene'}
                    </div>
                    {frame.video_url && <div className="frame-video-badge">✓ Video</div>}
                  </div>
                ))}
              </div>
              
              {/* Selected Frame Details */}
              {selectedFrame !== null && frames[selectedFrame] && (
                <div className="frame-details">
                  <h4>Frame {frames[selectedFrame].segment_number} Details</h4>
                  <div className="frame-detail-meta-row">
                    <div className="frame-meta-item">
                      <span className="label">Time:</span>
                      <span>{frames[selectedFrame].start_time} - {frames[selectedFrame].end_time}</span>
                    </div>
                    <div className="frame-meta-item">
                      <span className="label">Actor:</span>
                      <span>{frames[selectedFrame].with_actor ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="frame-meta-item">
                      <span className="label">Status:</span>
                      <span className={`status-${frames[selectedFrame].status || 'pending'}`}>{(frames[selectedFrame].status || 'pending').replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  {frames[selectedFrame].error_message && (
                    <div className="frame-detail-row">
                      <span className="label">Error:</span>
                      <span className="text-red">{frames[selectedFrame].error_message}</span>
                    </div>
                  )}
                  
                  {/* Regenerate button for failed frames */}
                  {frames[selectedFrame].status === 'failed' && (
                    <>
                      {/* If image exists, regenerate video; otherwise regenerate image */}
                      {frames[selectedFrame].image_base64 || frames[selectedFrame].image_url ? (
                        <button 
                          className="btn-outline btn-small mt-2"
                          onClick={() => onRegenerateVideo(frames[selectedFrame].segment_number)}
                          disabled={drawingFrame !== null || regeneratingImage !== null || frames[selectedFrame].regeneration_in_progress}
                        >
                          {drawingFrame === frames[selectedFrame].segment_number ? (
                            <><span className="spinner" /> Regenerating Video...</>
                          ) : (
                            'Regenerate Video'
                          )}
                        </button>
                      ) : (
                        <button 
                          className="btn-outline btn-small mt-2"
                          onClick={() => handleDrawSingleFrame(frames[selectedFrame].segment_number)}
                          disabled={drawingFrame !== null}
                        >
                          {drawingFrame === frames[selectedFrame].segment_number ? (
                            <><span className="spinner" /> Regenerating Image...</>
                          ) : (
                            'Regenerate Image'
                          )}
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Regenerate Video button for frames with image_ready status but has error (video failed) */}
                  {frames[selectedFrame].status === 'image_ready' && frames[selectedFrame].error_message && !frames[selectedFrame].regeneration_in_progress && (
                    <button 
                      className="btn-outline btn-small mt-2"
                      onClick={() => onRegenerateVideo(frames[selectedFrame].segment_number)}
                      disabled={drawingFrame !== null || regeneratingImage !== null || frames[selectedFrame].regeneration_in_progress}
                    >
                      {drawingFrame === frames[selectedFrame].segment_number ? (
                        <><span className="spinner" /> Regenerating Video...</>
                      ) : (
                        'Regenerate Video'
                      )}
                    </button>
                  )}
                  
                  {/* Also show Regenerate Video for video_ready frames that need re-generation (optional) */}
                  {(frames[selectedFrame].status === 'image_ready' && !frames[selectedFrame].error_message && !frames[selectedFrame].video_url && effectiveStep === 3) && (
                    <button 
                      className="btn-outline btn-small mt-2"
                      onClick={() => onRegenerateVideo(frames[selectedFrame].segment_number)}
                      disabled={drawingFrame !== null || isGeneratingVideos || regeneratingImage !== null || frames[selectedFrame].regeneration_in_progress}
                    >
                      Generate Video
                    </button>
                  )}
                  
                  {/* Regenerate Image button - show if:
                      1. Image is ready AND hasn't been successfully regenerated yet, OR
                      2. Previous regeneration failed (regeneration_failed: true) - allow retry
                  */}
                  {((frames[selectedFrame].status === 'image_ready' || frames[selectedFrame].status === 'video_ready') && 
                   !frames[selectedFrame].image_regenerated && 
                   !frames[selectedFrame].regeneration_in_progress &&
                   frames[selectedFrame].image_base64) || 
                   frames[selectedFrame].regeneration_failed ? (
                    <button 
                      className="btn-outline btn-small mt-2"
                      onClick={async () => {
                        const isRetry = frames[selectedFrame].regeneration_failed;
                        const confirmMsg = isRetry 
                          ? 'Previous regeneration failed. Try again?' 
                          : 'Regenerate this image? You can only regenerate each frame once. The video will need to be re-generated after.';
                        if (window.confirm(confirmMsg)) {
                          setRegeneratingImage(frames[selectedFrame].segment_number);
                          try {
                            await onRegenerateImage(frames[selectedFrame].segment_number);
                          } finally {
                            setRegeneratingImage(null);
                          }
                        }
                      }}
                      disabled={regeneratingImage !== null || drawingFrame !== null || isGeneratingVideos || frames[selectedFrame].regeneration_in_progress}
                    >
                      {regeneratingImage === frames[selectedFrame].segment_number || frames[selectedFrame].regeneration_in_progress ? (
                        <>
                          <RefreshCw size={14} className="spin inline mr-1" />
                          Regenerating...
                        </>
                      ) : frames[selectedFrame].regeneration_failed ? (
                        <>
                          <RefreshCw size={14} className="inline mr-1" />
                          Retry Regenerate Image
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} className="inline mr-1" />
                          Regenerate Image (1x)
                        </>
                      )}
                    </button>
                  ) : null}
                  
                  {/* Show indicator if frame was already regenerated */}
                  {frames[selectedFrame].image_regenerated && (
                    <div className="text-xs text-subtle mt-2">
                      Image was regenerated
                    </div>
                  )}
                  
                  {/* Show warning if regeneration is in progress */}
                  {frames[selectedFrame].regeneration_in_progress && (
                    <div className="text-xs text-yellow mt-2">
                      ⏳ Regeneration in progress...
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Wizard Footer */}
        <div className="video-wizard-footer">
          {/* Step 1: Upload Photo - Next goes to step 2 - only show if no currentTask */}
          {effectiveStep === 1 && !currentTask && (
            <button
              className="btn-primary"
              disabled={!uploadedPhoto}
              onClick={() => setStep(2)}
            >
              Next →
            </button>
          )}
          
          {/* Step 2: Select Aspect Ratio - Generate Story - only show if no currentTask */}
          {effectiveStep === 2 && !currentTask && (
            <button
              className="btn-primary"
              disabled={!aspectRatio || generatingStory}
              onClick={onGenerateStory}
            >
              {generatingStory ? (
                <>
                  <span className="spinner" />
                  Generating Story...
                </>
              ) : (
                'Generate Story →'
              )}
            </button>
          )}
          
          {/* Step 3: Draw Frames - Show button only when not processing */}
          {effectiveStep === 3 && !isDrawingImages && !isGeneratingVideos && !allImagesReady && (
            <button 
              className="btn-primary"
              onClick={onDrawAllFrames}
            >
              Draw All Frames
            </button>
          )}
          
          {/* Step 3: Drawing in progress */}
          {effectiveStep === 3 && isDrawingImages && (
            <button className="btn-primary" disabled>
              <span className="spinner" /> Processing... {imageProgress}% ({imagesReady}/{totalFrames})
            </button>
          )}
          
          {/* Step 3: All images ready, show Generate Videos button */}
          {effectiveStep === 3 && allImagesReady && !isGeneratingVideos && (
            <button 
              className="btn-primary"
              onClick={onGenerateVideos}
              disabled={anyRegenerationInProgress || regeneratingImage !== null}
            >
              {anyRegenerationInProgress || regeneratingImage !== null ? (
                <>⏳ Wait for regeneration...</>
              ) : (
                <>Generate Videos →</>
              )}
            </button>
          )}
          
          {/* Step 4: Generate Videos in progress */}
          {effectiveStep === 4 && isGeneratingVideos && (
            <button className="btn-primary" disabled>
              <span className="spinner" /> 
              {currentTask?.status === 'stitching' 
                ? 'Stitching Final Video...' 
                : `Generating Videos... ${videoProgress}% (${videosReady}/${totalFrames})`
              }
            </button>
          )}
          
          {/* Step 4: All images ready but not yet started video generation */}
          {effectiveStep === 4 && !isGeneratingVideos && !allVideosReady && (
            <button 
              className="btn-primary"
              onClick={onGenerateVideos}
              disabled={anyRegenerationInProgress || regeneratingImage !== null}
            >
              {anyRegenerationInProgress || regeneratingImage !== null ? (
                <>⏳ Wait for regeneration...</>
              ) : (
                <>Generate Videos →</>
              )}
            </button>
          )}
          
          {/* Step 5: Complete */}
          {(effectiveStep === 5 || currentTask?.status === 'completed') && (
            <div className="clip-complete-footer">
              <span className="text-green">✓ Clip Complete!</span>
              <button className="btn-primary" onClick={onClose}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
